import { inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withHooks, withMethods, withState } from '@ngrx/signals';
import { cloneDeep } from 'lodash';
import { filter } from 'rxjs';
import { ChannelService, ChatClientService } from 'stream-chat-angular';
import { ChatDataStore } from '../../chat/chat-data.store';
import { userState } from '../user/user.feature';

export function withLatestMessageTime() {
  return signalStoreFeature(
    { state: type<userState>() }, // Must be used in a store that has the user state
    withState({
      latestMessageTime: null as Date | null,
    }),
    withMethods((store) => {
      const chatDataStore = inject(ChatDataStore);
      const channelService = inject(ChannelService);
      return {
        updateLatestMessage: () => {
          const incompleteChats = chatDataStore.incompleteChats();

          const channels = channelService.channels.filter((channel) =>
            incompleteChats.some((chat) => chat.channel_id === channel.id)
          );

          const latestMessageTime = channels.reduce((acc: Date | null, channel) => {
            const channelMessages = cloneDeep(channel?.state.messages);
            if (!channelMessages || channelMessages.length === 0) {
              return acc;
            }
            const lastMessage = channelMessages
              .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
              .filter((x) => x.user?.id !== store.user()?.doc_id || '')[0];
            return acc && acc > lastMessage.created_at ? acc : lastMessage.created_at;
          }, null);
          patchState(store, { latestMessageTime });
        },
      };
    }),
    withHooks((store) => ({
      onInit: () => {
        const chatClientService = inject(ChatClientService);
        store.updateLatestMessage();
        chatClientService.events$
          .pipe(
            takeUntilDestroyed(),
            filter((e) => {
              const events = ['message.new', 'message.updated', 'message.deleted', 'message.read', 'channel.truncated'];
              return events.includes(e.eventType);
            }),
            tapResponse({
              next: () => {
                store.updateLatestMessage();
              },
              error: (error) => {
                console.error('Error updating latest message time:', error);
              },
            })
          )
          .subscribe();
      },
    }))
  );
}
